import React, {forwardRef, useEffect, useRef, useState, useCallback} from "react";
import {Box, Grid, TextField, Paper, Typography, InputAdornment, IconButton} from "@mui/material";
import {useTranslation} from "react-i18next";
import {Controller, useForm, useWatch} from "react-hook-form"
import WebURL from "@/urls";
import CustomBreadcrumbs from "@/components/breadcrumbs/CustomBreadcrumbs";
import {zodResolver} from "@hookform/resolvers/zod";
import {useSnackbar} from "notistack";
import {number, object, TypeOf, z} from "zod";
import EmpPicker from '@/components/EmpPicker';
import {CancelButton, SaveButton} from "@/components/data-grid/CustomButton";
import NGInputLabel from "@/components/ngteco-form/NGInputLabel";
import {useLocation, useNavigate} from "react-router-dom";
import {
  GoogleMap, LoadScript,
  Autocomplete, useJsApiLoader,
  MarkerF, CircleF
} from '@react-google-maps/api';
import ControlledTextFiled from "@/components/ngteco-hook-form/ControlledTextFiled";
import MobileService from "@/services/att/services/MobileService";


const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

const mapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
};

const NewClockAddress: React.FC = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const location = useLocation()
  const state = location.state as { id: string }
  const [title, setTitle] = useState("")
  const LocationAddValidate = object({
    employee_name: z.string().optional(),
    employeeObj: z.array(
      z.object({
        id: z.string(),
        firstName: z.string(),
        lastName: z.string(),
      }),
      {required_error: t("common.This field is required!")}
    ),
    gps_location: z.string().min(1, {message: t("common.address is required")}),
    clock_range: z.string()
      .min(1, {message: t("common.clock range need integer")})
      .max(10000, {message: t("common.clock range need in 1-10000")}),
    longitude:
      z.string(),
    latitude:
      z.string(),
  })
  const [address, setAddress] = React.useState<string>('')
  const [range, setRange] = React.useState<number>(100)
  const [centerData, setCenterData] = React.useState<any>({
    lat: 37.7749,
    lng: -122.4194,
  })

  useEffect(() => {
    if (state?.id) {
      setTitle(t("common.Edit Punch Setting"))
      MobileService.retrieve(state.id || "").then((res) => {
        const data = res.data.data[0];
        locationAddForm.reset({
          gps_location: data.gps_location,
          clock_range: data.clock_range.toString(),
          longitude: data.longitude,
          latitude: data.latitude,
          employeeObj: data.emp_info.map((emp: any) => ({
            id: emp.employee_id,
            firstName: emp.first_name,
            lastName: emp.last_name,
          })),
        });
        setCenterData({
          lat: parseFloat(res.data.data[0]?.latitude),
          lng: parseFloat(res.data.data[0]?.longitude)
        })
        setRange(parseInt(res.data.data[0]?.clock_range))
        // setOldData({
        //   code: res.data.data?.code,
        //   name: res.data.data?.name
        // })
      });
    } else {
      setTitle(t("common.New Punch Setting"))
    }
  }, [state]);

  const handleMapClick = (event: any) => {
    console.log('Map clicked at:', event.latLng.lat(), event.latLng.lng());
    const click_lat = event.latLng.lat();
    const click_lng = event.latLng.lng();
    locationAddForm.setValue('latitude', click_lat.toString())
    locationAddForm.setValue('longitude', click_lng.toString())
    setCenterData({
      lat: click_lat,
      lng: click_lng
    })
    new window.google.maps.Geocoder().geocode(
      {location: {lat: click_lat, lng: click_lng}},
      (results, status) => {
        if (status === 'OK' && results) {
          locationAddForm.setValue('gps_location', results[0].formatted_address);
          setAddress(results[0].formatted_address)
        }
      }
    );
  };

  type LocationAddForm = TypeOf<typeof LocationAddValidate>;

  const locationAddForm = useForm<LocationAddForm>({
    resolver: zodResolver(LocationAddValidate),
    defaultValues: {
      latitude: "37.7749",
      longitude: "-122.4194",
      clock_range: "100",
    }
  })

  const {enqueueSnackbar} = useSnackbar();
  const clockRangeValue = useWatch({
    control: locationAddForm.control,
    name: "clock_range",
    defaultValue: "100",
  });

  useEffect(() => {
    setRange(Number(clockRangeValue));
  }, [clockRangeValue]);
  const handleSubmit = async () => {
    const data = locationAddForm.getValues()
    if (data.clock_range.includes('.')){
      enqueueSnackbar(t("common.clock range need integer"), {
        variant: "error",
        anchorOrigin: {
          vertical: "top",
          horizontal: "center"
        }
      });
      return;
    }
    const clock_range = Number(data.clock_range);
    if (clock_range < 1 || clock_range > 10000) {
      enqueueSnackbar(t("common.clock range need in 1-10000"), {
        variant: "error",
        anchorOrigin: {
          vertical: "top",
          horizontal: "center"
        }
      });
      return;
    }
    const gps_location = data.gps_location;
    if (!gps_location) {
      enqueueSnackbar(t("common.address is required"), {
        variant: "error",
        anchorOrigin: {
          vertical: "top",
          horizontal: "center"
        }
      });
      return;
    }
    const employeeObj = data.employeeObj;
    if (!employeeObj || employeeObj.length === 0) {
      enqueueSnackbar(t("common.use scope is required"), {
        variant: "error",
        anchorOrigin: {
          vertical: "top",
          horizontal: "center"
        }
      });
      return;
    }
    // data.employee = data.employeeObj?.id
    data.employeeObj = data.employeeObj.map((emp: any) => emp?.id)
    try {
      if (state?.id) {
        await MobileService.update(state.id, data);
      } else {
        await MobileService.addLocation(data);
      }

      locationAddForm.reset();
      enqueueSnackbar(t("common.success"), {
        variant: "success",
        anchorOrigin: {vertical: "top", horizontal: "center"}
      });

      navigate(WebURL.ATT_CLOCK_ADDRESS, {
        state: {needRefresh: true}
      });

    } catch (error) {
      console.error("操作失败:", error);
    }
  }
  const {isLoaded} = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: 'AIzaSyA9MaTVJlWIWpINjcgyJl5eS6JDhe60238',
    libraries: ['places', 'geometry'],
    // language: 'zh'
  });
  useEffect(() => {
    if (LoadScript && address && google && google.maps.places) {
      const autocompleteService = new google.maps.places.AutocompleteService();
      const geocoder = new google.maps.Geocoder();
      autocompleteService.getPlacePredictions(
        {
          input: address,
          bounds: {
            north: centerData?.lat || -34.397 + 0.1,
            south: centerData?.lat || -34.397 - 0.1,
            east: centerData?.lng || 150.644 + 0.1,
            west: centerData?.lng || 150.644 - 0.1,
          },
        },
        (predictions, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
            geocoder.geocode(
              {
                placeId: predictions[0].place_id,
              },
              function (responses, status) {
                if (status == 'OK' && responses && (responses).length > 0) {
                  const address_components = responses[0].address_components
                  let country = ''
                  for (let i = 0; i < address_components.length; i++) {
                    const types = address_components[i].types
                    const short_name = address_components[i].short_name
                    if (types.includes('country')) {
                      country = short_name
                      break
                    }
                  }
                  const lat1 = responses[0].geometry.location.lat().toString();
                  const lng1 = responses[0].geometry.location.lng().toString();
                  locationAddForm.setValue("longitude", lng1)
                  locationAddForm.setValue("latitude", lat1)
                  setCenterData({
                    lat: lat1,
                    lng: lng1
                  })
                }
              },
            );
          }
        },
      );
    }
  }, [LoadScript, address]);

  const onLoad = useCallback(function callback(map: any) {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(function (position) {
        setCenterData({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      }, (positionError: GeolocationPositionError) => {
        console.log(positionError)
        setCenterData({
          lat: 37.7749,
          lng: -122.4194,
        })
      });
    }
  }, []);

  return (
    <Grid
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        padding: "24px",
        width: "calc(100% - 24px)",
        backgroundColor: "#ffffff",
        height: "calc(100vh - 90px)",
        overflow: 'auto',
        position: 'relative',
      }}>
      <CustomBreadcrumbs record={[{label: title}]}/>
      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          {isLoaded ? (<GoogleMap
            mapContainerStyle={mapContainerStyle}
            zoom={14}
            center={centerData}
            options={mapOptions}
            onLoad={onLoad}
            onClick={handleMapClick}
          >
            <MarkerF position={centerData} />
            <CircleF
              center={centerData}
              radius={Math.max(range, 1)}
              options={{
                fillColor: "#2196f3",
                fillOpacity: 0.2,
                strokeColor: "#2196f3",
                strokeWeight: 2,
              }}
            />
          </GoogleMap>
        ) : null}
        </Grid>
        <Grid item xs={12} md={1}></Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={0}>
            <Grid container rowSpacing={2} columnSpacing={2}>
              <Grid item xs={12}>
                <Grid container={true}>
                  <Grid xs={12}>
                    <Grid sx={{paddingTop: '10px'}}>
                      {isLoaded ? <Autocomplete
                        options={{
                          bounds: {
                            north: centerData?.lat || -34.397 + 0.1,
                            south: centerData?.lat || -34.397 - 0.1,
                            east: centerData?.lng || 150.644 + 0.1,
                            west: centerData?.lng || 150.644 - 0.1,
                          },
                          fields: ['geometry', 'name', 'address_components'],
                          strictBounds: false,
                          // types: ['address'],
                        }}
                        onLoad={(autocomplete) => {
                          autocomplete.addListener('place_changed', () => {
                            const place = autocomplete.getPlace();
                            const address_components = place.address_components || []
                            let country = ''
                            for (let i = 0; i < address_components.length; i++) {
                              const types = address_components[i].types
                              const short_name = address_components[i].short_name
                              if (types.includes('country')) {
                                country = short_name
                                break
                              }
                            }
                            const name = place?.name;
                            const latitude = place.geometry?.location?.lat() || '';
                            const longitude = place.geometry?.location?.lng() || '';
                            setCenterData({
                              lat: latitude,
                              lng: longitude
                            })
                            setAddress(name || '')
                            locationAddForm.setValue('latitude', latitude.toString())
                            locationAddForm.setValue('longitude', longitude.toString())
                            locationAddForm.setValue('gps_location', name || '')
                          });
                        }}
                      >
                        <ControlledTextFiled
                          hookForm={locationAddForm}
                          label={t("common.address")}
                          name="gps_location"
                          fullWidth
                          required
                          placeholder={t("common.address")}
                        />
                      </Autocomplete> : <></>}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid container={true} rowSpacing={2} columnSpacing={2}>
                  <Grid item={true} xs={12}>
                    <Box sx={{marginBottom: '8px'}}>
                      <Grid sx={{display: 'flex'}}>
                        <Grid>
                          <NGInputLabel label={t("common.scope of use")}/>
                        </Grid>
                        <Grid>
                          <Typography sx={{marginLeft: "3px"}} color={"red"}>*</Typography>
                        </Grid>
                      </Grid>
                    </Box>
                    <Controller
                      name="employeeObj"
                      control={locationAddForm.control}
                      render={({field, fieldState}) =>
                        <EmpPicker
                          error={!!fieldState.error}
                          value={field.value}
                          onChange={field.onChange}
                          multiline={true}
                          helperText={fieldState.error?.message || ""}
                          multi={true}
                        />
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid sx={{paddingTop: '10px'}}>
                  <ControlledTextFiled
                    hookForm={locationAddForm}
                    label={t("common.clock range")}
                    name="clock_range"
                    fullWidth
                    required
                    type={'number'}
                    placeholder={t("common.clock range")}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">
                        <IconButton sx={{ marginRight: "-10px" }}>
                         m
                        </IconButton>
                      </InputAdornment>
                    }}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid sx={{paddingTop: '10px'}}>
                  <ControlledTextFiled
                    hookForm={locationAddForm}
                    label={t("common.longitude")}
                    name="longitude"
                    fullWidth
                    required
                    disabled={true}
                    // placeholder={t("common.longitude")}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid sx={{paddingTop: '10px'}}>
                  <ControlledTextFiled
                    hookForm={locationAddForm}
                    label={t("common.latitude")}
                    name="latitude"
                    fullWidth
                    required
                    disabled={true}
                    // placeholder={t("common.latitude")}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Paper>
          <Grid item
                sx={{
                  borderTop: '1px solid #F2F5F8',
                  position: 'absolute',
                  bottom: '12px',
                  left: '0',
                  width: '100%'
                }}>
            <CancelButton
              sx={{textTransform: "capitalize", width: '160px', marginTop: '12px', marginLeft: '24px'}}/>
            <SaveButton
              fullWidth
              onClick={locationAddForm.handleSubmit(handleSubmit)}
              disableElevation
              sx={{textTransform: "capitalize", width: '160px', marginTop: '12px', marginLeft: '24px'}}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default NewClockAddress;

